/**
 * The speed slider's scale (spec 035 D-1): a 0–100 position whose left half
 * runs linearly from 0.25× to 1× and right half from 1× to 3×, snapping to
 * exactly 1× within 0.04 of it, so Normal sits in the middle; and the plain
 * label of a speed. Pure.
 */
import type { SheetCut, StatePlayback } from '../../../Shared/src/@types';
import { frameDurations, PLAYBACK_SPEED_MAX, PLAYBACK_SPEED_MIN } from '../../../Shared/src/utils/spriteSheet';

const SNAP = 0.04;
const round2 = (v: number) => Math.round(v * 100) / 100;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The speed at slider position `p` (0–100), to two decimals. */
export function speedFromPosition(p: number): number {
  const x = clamp(Number.isFinite(p) ? p : 50, 0, 100);
  const raw = x <= 50
    ? PLAYBACK_SPEED_MIN + (x / 50) * (1 - PLAYBACK_SPEED_MIN)
    : 1 + ((x - 50) / 50) * (PLAYBACK_SPEED_MAX - 1);
  if (Math.abs(raw - 1) <= SNAP + 1e-9) return 1;
  return round2(raw);
}

/** The slider position (0–100) of speed `v` (absent = 1). */
export function positionFromSpeed(v: number | undefined): number {
  const s = clamp(typeof v === 'number' && Number.isFinite(v) ? v : 1, PLAYBACK_SPEED_MIN, PLAYBACK_SPEED_MAX);
  return s <= 1
    ? ((s - PLAYBACK_SPEED_MIN) / (1 - PLAYBACK_SPEED_MIN)) * 50
    : 50 + ((s - 1) / (PLAYBACK_SPEED_MAX - 1)) * 50;
}

export type SpeedLabel = 'Slowest' | 'Slower' | 'Slow' | 'Normal' | 'Fast' | 'Faster' | 'Fastest';

/** The plain label of speed `v` (absent = 1). */
export function speedLabel(v: number | undefined): SpeedLabel {
  const s = typeof v === 'number' && Number.isFinite(v) ? v : 1;
  if (s < 0.4) return 'Slowest';
  if (s < 0.65) return 'Slower';
  if (s < 0.9) return 'Slow';
  if (s <= 1.1) return 'Normal';
  if (s < 1.6) return 'Fast';
  if (s < 2.3) return 'Faster';
  return 'Fastest';
}

/** "1.5×", "2×", "0.63×". */
export const speedText = (v: number | undefined): string => `${Number((v ?? 1).toFixed(2))}×`;

/** The frames a playback plays, 0-based inclusive: its `range` clamped into the cut, else all. */
export function playedRange(cut: SheetCut, playback: StatePlayback | undefined): [number, number] {
  const last = Math.max(0, cut.frameCount - 1);
  const r = playback?.range;
  if (!r) return [0, last];
  const a = clamp(Math.round(r[0]), 0, last);
  return [a, clamp(Math.round(r[1]), a, last)];
}

/** One cycle of a playback at its speed: frames in the range, the rate and the cycle's length. */
export function cycleInfo(cut: SheetCut, playback: StatePlayback | undefined): { fps: number; frames: number; seconds: number } {
  const [a, b] = playedRange(cut, playback);
  const durations = frameDurations(cut, playback?.speed).slice(a, b + 1);
  const forward = durations.reduce((x, y) => x + y, 0);
  const back = playback?.mode === 'pingpong' ? durations.slice(1, -1).reduce((x, y) => x + y, 0) : 0;
  const mean = forward / Math.max(1, durations.length);
  return { fps: mean > 0 ? 1000 / mean : 0, frames: durations.length, seconds: (forward + back) / 1000 };
}

/** "24 fps · 8 frames · 0.33 s per cycle". */
export function cycleText(cut: SheetCut, playback: StatePlayback | undefined): string {
  const { fps, frames, seconds } = cycleInfo(cut, playback);
  return `${Number(fps.toFixed(1))} fps · ${frames} ${frames === 1 ? 'frame' : 'frames'} · ${Number(seconds.toFixed(2))} s per cycle`;
}
